import { Component } from "react";
import { reloadOnceForChunkError } from "../lib/lazyWithReload";

/**
 * Global error boundary — catches any uncaught React render error and
 * shows a friendly recovery screen instead of a fully blank page. The
 * goal isn't to hide bugs but to always give the user an escape hatch
 * (reload, wipe cache, contact support) rather than a mysterious blank
 * canvas that looks like the app is dead.
 */
export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (reloadOnceForChunkError(error)) {
      this.setState({ reloading: true });
      return;
    }
    // Surface to devtools + best-effort remote log.
    // eslint-disable-next-line no-console
    console.error("[Casanéo] uncaught render error:", error, info?.componentStack);
    try {
      const beacon = new Blob(
        [
          JSON.stringify({
            message: String(error?.message || error),
            stack: String(error?.stack || "").slice(0, 4000),
            url: window.location.href,
            ua: navigator.userAgent,
            at: new Date().toISOString(),
          }),
        ],
        { type: "application/json" }
      );
      navigator.sendBeacon?.(
        `${process.env.REACT_APP_BACKEND_URL}/api/local/client-error`,
        beacon
      );
    } catch {
      /* noop — best effort */
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.state.reloading) {
      return (
        <div className="min-h-screen bg-[#071e42] text-slate-300 grid place-items-center text-sm" data-testid="app-updating">
          Mise à jour de l'application…
        </div>
      );
    }
    return (
      <div
        className="min-h-screen bg-[#071e42] text-slate-100 grid place-items-center px-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-lg text-center">
          <img
            src="/casaneo-logo.jpg"
            alt="Casanéo"
            className="h-14 w-auto rounded-lg mx-auto mb-6"
          />
          <h1 className="font-serif text-3xl text-white">
            Un accroc s'est glissé
          </h1>
          <p className="mt-3 text-slate-300">
            Une erreur inattendue est survenue. Aucune donnée n'a été perdue —
            tes informations sont bien enregistrées côté serveur.
          </p>
          <details className="mt-4 text-left text-xs text-slate-400 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2">
            <summary className="cursor-pointer">Détails techniques</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          </details>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="rounded-full bg-brass hover:bg-brass/90 text-[#0b1a35] font-semibold px-6 py-3 text-sm transition-transform hover:scale-[1.02]"
              data-testid="app-error-reload"
            >
              Recharger la page
            </button>
            <a
              href="/reset"
              className="rounded-full border border-white/20 hover:border-white/40 px-6 py-3 text-sm text-slate-200 hover:text-white"
              data-testid="app-error-reset"
            >
              Réinitialiser l'application
            </a>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Le problème persiste ? Contacte-nous à{" "}
            <a
              className="underline"
              href="mailto:contact@casaneo.pro?subject=Casaneo%20—%20erreur%20applicative"
            >
              contact@casaneo.pro
            </a>{" "}
            avec un screenshot de cet écran.
          </p>
        </div>
      </div>
    );
  }
}
